import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { useCan } from '../../lib/session.js';
import { Absent, Pill } from '../../components/audit/AuditVocabulary.js';
import { ApiError, describeError } from '../../lib/api.js';
import {
  approveSchedule,
  createSchedule,
  fetchApprovalMemo,
  fetchPlans,
  fetchSchedules,
  sendBackSchedule,
  submitSchedule,
  updateSchedule,
  type AuditSchedule,
} from '../../lib/audit-api.js';
import { downloadDocument } from '../../lib/documents-api.js';
import { t, formatDate } from '../../lib/i18n.js';

/**
 * The Annual Audit Schedule builder and the Director/CEO approval dashboard
 * (spec §11 key screens 1 and 2).
 *
 * They are one screen rather than two because they are one queue seen from two
 * sides: the Audit Program Manager builds and submits, the approver approves or
 * sends back, and both need the same context — which audits the programme
 * actually commits to. Splitting them would mean the approver decides on a title
 * and a year.
 *
 * The buttons a user cannot use are **hidden, not disabled** (spec §14). A
 * disabled Approve button tells a Programme Manager the system thinks they might
 * approve their own work.
 */
export function AuditSchedules() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [sendingBack, setSendingBack] = useState<string | null>(null);
  // Offered only to those who hold them somewhere; the server still decides (pass 9).
  const canEdit = useCan('audit', 'create_edit');
  const canSubmit = useCan('audit', 'submit');
  const canApprove = useCan('audit', 'approve_reject');
  const [remarks, setRemarks] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear() + 1));
  const [title, setTitle] = useState('');

  const schedules = useQuery({ queryKey: ['audit-schedules'], queryFn: fetchSchedules });
  const plans = useQuery({ queryKey: ['audit-plans'], queryFn: () => fetchPlans() });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['audit-schedules'] });
    void queryClient.invalidateQueries({ queryKey: ['audit-plans'] });
  };

  const fail = (fallback: string) => (caught: unknown) =>
    setError(describeError(caught, fallback));

  const create = useMutation({
    mutationFn: () => createSchedule({ year: Number(year), title }),
    onSuccess: () => {
      setError(null);
      setTitle('');
      refresh();
    },
    onError: fail(t("That schedule could not be created.")),
  });

  const submit = useMutation({
    mutationFn: (id: string) => submitSchedule(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("That schedule could not be submitted.")),
  });

  const approve = useMutation({
    mutationFn: (id: string) => approveSchedule(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("That schedule could not be approved.")),
  });

  const sendBack = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => sendBackSchedule(id, text),
    onSuccess: () => {
      setError(null);
      setSendingBack(null);
      setRemarks('');
      refresh();
    },
    onError: fail(t("That schedule could not be sent back.")),
  });

  const rows: AuditSchedule[] = schedules.data?.schedules ?? [];
  const plannedFor = (scheduleId: string) =>
    (plans.data?.plans ?? []).filter((plan) => plan.scheduleId === scheduleId);

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Annual schedule']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Annual audit schedule")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("One programme per year. It needs at least one planned audit before it can be submitted — a programme committing to nothing reads like a commitment.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        {canEdit && (
        <form
          className="mt-6 rounded-md border border-border bg-surface p-5"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <h2 className="m-0 text-base font-semibold">{t("Start a programme")}</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Year")}</span>
              <input
                className="w-28 rounded border border-border px-2 py-1"
                inputMode="numeric"
                value={year}
                onChange={(event) => setYear(event.target.value)}
              />
            </label>
            <label className="flex flex-1 flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Title")}</span>
              <input
                className="rounded border border-border px-2 py-1"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={t("Internal audit programme")}
              />
            </label>
            <button
              type="submit"
              className="rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
              disabled={create.isPending || !title.trim()}
            >
              {t("Create")}
            </button>
          </div>
        </form>
        )}

        {schedules.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!schedules.isLoading && rows.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("No audit programme has been started yet.")}
          </p>
        )}

        <ul className="mt-6 list-none space-y-4 p-0">
          {rows.map((schedule) => {
            const planned = plannedFor(schedule.id);
            return (
              <li
                key={schedule.id}
                className="rounded-md border border-border bg-surface p-5 shadow-card"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="m-0 text-base font-semibold">
                    {schedule.year} — {schedule.title}
                  </h2>
                  <Pill tone={schedule.status === 'approved' ? 'positive' : 'neutral'}>
                    {schedule.status.replace('_', ' ')}
                  </Pill>
                </div>

                <p className="mt-2 text-sm text-content-secondary">
                  {planned.length === 0
                    ? t("No audits planned yet.")
                    : t("{0} audit{1} planned.", { 0: planned.length, 1: planned.length === 1 ? '' : 's' })}
                </p>

                {schedule.sentBackRemarks && (
                  <p className="mt-2 rounded border border-border bg-tone-info-surface p-3 text-sm">
                    <strong>{t("Sent back:")}</strong> {schedule.sentBackRemarks}
                  </p>
                )}

                {schedule.status === 'approved' && (
                  <p className="mt-2 text-sm text-content-secondary">
                    {t("Memo distributed:")}{' '}
                    {schedule.memoDistributedAt ? (
                      formatDate(schedule.memoDistributedAt)
                    ) : schedule.memoFileId ? (
                      /* The memo PDF exists, but no mail server has accepted it —
                         either no distribution list is set up for it, or email is
                         record-only. "Distributed" is stamped only on acceptance. */
                      <Absent reason={t("The memo is kept as a PDF but has not been sent — see the email outbox.")} />
                    ) : (
                      <Absent reason={t("The memo is being prepared — the distribution job makes it shortly after approval.")} />
                    )}
                  </p>
                )}

                {schedule.status === 'approved' && schedule.memoFileId && <MemoLink schedule={schedule} />}

                {(schedule.status === 'draft' || schedule.status === 'sent_back') && canEdit && (
                  <EditSchedule schedule={schedule} onSaved={() => void queryClient.invalidateQueries({ queryKey: ['audit-schedules'] })} />
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {(schedule.status === 'draft' || schedule.status === 'sent_back') && canSubmit && (
                    <button
                      type="button"
                      className="rounded border border-border px-3 py-1.5 text-sm"
                      onClick={() => submit.mutate(schedule.id)}
                      disabled={submit.isPending}
                    >
                      {t("Submit for approval")}
                    </button>
                  )}

                  {schedule.status === 'submitted' && canApprove && (
                    <>
                      <button
                        type="button"
                        className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white"
                        onClick={() => approve.mutate(schedule.id)}
                        disabled={approve.isPending}
                      >
                        {t("Approve")}
                      </button>
                      <button
                        type="button"
                        className="rounded border border-border px-3 py-1.5 text-sm"
                        onClick={() => setSendingBack(schedule.id)}
                      >
                        {t("Send back")}
                      </button>
                    </>
                  )}
                </div>

                {sendingBack === schedule.id && (
                  <form
                    className="mt-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      sendBack.mutate({ id: schedule.id, text: remarks });
                    }}
                  >
                    <label className="flex flex-col text-sm">
                      <span className="mb-1 text-content-secondary">
                        {t("What needs to change? Required — a verdict is not a correction.")}
                      </span>
                      <textarea
                        className="rounded border border-border px-2 py-1"
                        rows={3}
                        value={remarks}
                        onChange={(event) => setRemarks(event.target.value)}
                      />
                    </label>
                    <button
                      type="submit"
                      className="mt-2 rounded border border-border px-3 py-1.5 text-sm"
                      disabled={!remarks.trim() || sendBack.isPending}
                    >
                      {t("Send back")}
                    </button>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The approved programme's memo, as the PDF kept on approval (FN-10). Opened
 * only through its report, which checks export authority; if the reader has
 * none, the link says so rather than failing silently.
 */
function MemoLink({ schedule }: { schedule: AuditSchedule }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <p className="mt-2 text-sm">
      <button
        type="button"
        className="text-ag-purple underline"
        onClick={() => {
          setError(null);
          void fetchApprovalMemo(schedule.id)
            .then((memo) => {
              if (!memo.issued) throw new Error(t("The memo PDF is not ready yet."));
              return downloadDocument(memo.issued.id, `audit-programme-${schedule.year}-memo.pdf`);
            })
            .catch((caught) => setError(caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : t("The memo could not be opened.")));
        }}
      >
        {t("Download the approval memo (PDF)")}
      </button>
      {error && (
        <span role="alert" className="ms-2" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </span>
      )}
    </p>
  );
}

/** A programme's year and title can change while it is a draft or sent back (FN-10). */
function EditSchedule({ schedule, onSaved }: { schedule: AuditSchedule; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(String(schedule.year));
  const [title, setTitle] = useState(schedule.title);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => updateSchedule(schedule.id, { year: Number(year), title: title.trim() }),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onSaved();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The programme could not be changed.")),
  });

  if (!open) {
    return (
      <button type="button" className="mt-2 rounded border border-border px-3 py-1 text-sm" onClick={() => setOpen(true)}>
        {t("Edit year or title")}
      </button>
    );
  }
  return (
    <form
      aria-label={t("Edit {0}", { 0: schedule.title })}
      className="mt-2 flex flex-wrap items-end gap-2 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <label className="flex flex-col">
        <span className="mb-1 text-content-secondary">{t("Year")}</span>
        <input type="number" className="w-24 rounded border border-border px-2 py-1" value={year} onChange={(event) => setYear(event.target.value)} />
      </label>
      <label className="flex flex-1 flex-col">
        <span className="mb-1 text-content-secondary">{t("Title")}</span>
        <input className="rounded border border-border px-2 py-1" value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <button type="submit" className="rounded bg-ag-purple px-3 py-1 text-white disabled:opacity-60" disabled={save.isPending || !title.trim()}>
        {t("Save")}
      </button>
      <button type="button" className="rounded border border-border px-3 py-1" onClick={() => setOpen(false)}>
        {t("Cancel")}
      </button>
      {error && (
        <p role="alert" className="m-0 w-full" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </form>
  );
}
